import { useEffect, useState } from 'react';

import { communityApi } from '../../api/community';
import type { CommentView, PostDetailView, ReportReason } from '../../api/community.types';
import { ErrorState, LoadingState } from '../../components/ScreenState';
import { useAsync } from '../../hooks/useAsync';
import { AuthorRow } from '../social/AuthorRow';
import { Avatar } from '../social/Avatar';
import { Composer } from '../social/Composer';
import { relativeTime, shortName } from '../social/format';
import { PostActions, PostAttachmentView, PostBadges, ReportDialog } from '../social/postParts';
import { useSocial } from '../social/SocialContext';
import { SocialHeader } from '../social/SocialHeader';
import { SocialIcon, SocialIconButton } from '../social/SocialIcon';

function CommentLink({ label, onClick, color = 'var(--text-secondary)' }: { label: string; onClick: () => void; color?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      // Chữ 12px nhưng vùng bấm đủ 44px: padding dọc bù lại, margin âm giữ dòng không giãn.
      style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '14px 0', margin: '-14px 0', fontFamily: 'var(--font-vi)', fontSize: 12, fontWeight: 600, color }}
    >
      {label}
    </button>
  );
}

function CommentRow({
  comment,
  last,
  canPickBest,
  mine,
  onLike,
  onReply,
  onPickBest,
  onDelete,
}: {
  comment: CommentView;
  last: boolean;
  canPickBest: boolean;
  mine: boolean;
  onLike: () => void;
  onReply: () => void;
  onPickBest: () => void;
  onDelete: () => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '12px 0', borderBottom: last ? 'none' : '1px solid var(--border-default)' }}>
      <Avatar name={comment.author.name} size={34} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>{comment.author.name}</span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{`${comment.author.level} · ${relativeTime(comment.createdAt)}`}</span>
        </div>
        {comment.isBest && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: 'var(--success)' }}>
            <SocialIcon name="check" size={14} />
            Người hỏi chọn là câu trả lời đúng
          </div>
        )}
        <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{comment.body}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 2 }}>
          <CommentLink
            label={`Thích${comment.likeCount ? ` · ${comment.likeCount}` : ''}`}
            color={comment.liked ? 'var(--navy-500)' : undefined}
            onClick={onLike}
          />
          <CommentLink label="Trả lời" onClick={onReply} />
          {canPickBest && <CommentLink label={comment.isBest ? 'Bỏ chọn' : 'Chọn là câu trả lời đúng'} onClick={onPickBest} />}
          {mine && <CommentLink label="Xoá" onClick={onDelete} />}
        </div>
      </div>
    </div>
  );
}

/**
 * SCR-COM-02 trong modal. `onChange` báo lại bản đã sửa (like, bình luận, đã giải đáp)
 * để thẻ bài trên bảng tin khớp số mà không phải tải lại cả trang.
 */
export function PostDetailPanel({
  postId,
  authorName,
  onClose,
  onChange,
}: {
  postId: string;
  authorName: string;
  onClose: () => void;
  onChange: (post: PostDetailView) => void;
}) {
  const { me, toast } = useSocial();
  const [reloadKey, setReloadKey] = useState(0);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const loaded = useAsync(() => communityApi.getPost(postId), [postId, reloadKey]);

  // Bản sửa tại chỗ của dữ liệu vừa tải — cùng cách reset trong render với useAsync.
  const [source, setSource] = useState<PostDetailView | null>(null);
  const [post, setPostState] = useState<PostDetailView | null>(null);
  if (loaded.data && loaded.data !== source) {
    setSource(loaded.data);
    setPostState(loaded.data);
  }

  const update = (fn: (p: PostDetailView) => PostDetailView) => setPostState((p) => p && fn(p));

  // Báo bảng tin mỗi khi có bản sửa tại chỗ (không báo lúc vừa tải xong — chưa có gì đổi).
  useEffect(() => {
    if (post && post !== source) onChange(post);
  }, [post, source, onChange]);

  const patchComment = (commentId: string, patch: Partial<CommentView>) =>
    update((p) => ({ ...p, comments: p.comments.map((c) => (c.id === commentId ? { ...c, ...patch } : c)) }));

  const likePost = async () => {
    if (!post) return;
    const liked = !post.liked;
    update((p) => ({ ...p, liked, likeCount: post.likeCount + (liked ? 1 : -1) }));
    try {
      const result = await (liked ? communityApi.likePost(post.id) : communityApi.unlikePost(post.id));
      update((p) => ({ ...p, ...result }));
    } catch {
      update((p) => ({ ...p, liked: post.liked, likeCount: post.likeCount }));
      toast('Chưa thích được, thử lại sau');
    }
  };

  const likeComment = async (comment: CommentView) => {
    const liked = !comment.liked;
    patchComment(comment.id, { liked, likeCount: comment.likeCount + (liked ? 1 : -1) });
    try {
      patchComment(comment.id, await (liked ? communityApi.likeComment(comment.id) : communityApi.unlikeComment(comment.id)));
    } catch {
      patchComment(comment.id, { liked: comment.liked, likeCount: comment.likeCount });
      toast('Chưa thích được, thử lại sau');
    }
  };

  const send = async () => {
    if (!post || !text.trim() || sending) return;
    setSending(true);
    try {
      const added = await communityApi.addComment(post.id, text.trim());
      update((p) => ({ ...p, comments: [...p.comments, added], commentCount: p.commentCount + 1 }));
      setText('');
    } catch {
      toast('Chưa gửi được bình luận, thử lại sau');
    } finally {
      setSending(false);
    }
  };

  const pickBest = async (comment: CommentView) => {
    try {
      const { isBest, solved } = await communityApi.toggleBestComment(comment.id);
      update((p) => ({ ...p, solved, comments: p.comments.map((c) => ({ ...c, isBest: c.id === comment.id && isBest })) }));
    } catch {
      toast('Chưa chọn được câu trả lời, thử lại sau');
    }
  };

  const removeComment = async (comment: CommentView) => {
    try {
      await communityApi.deleteComment(comment.id);
      update((p) => ({
        ...p,
        comments: p.comments.filter((c) => c.id !== comment.id),
        commentCount: p.commentCount - 1,
        solved: comment.isBest ? false : p.solved,
      }));
    } catch {
      toast('Chưa xoá được bình luận, thử lại sau');
    }
  };

  const report = async (reason: ReportReason) => {
    setReportOpen(false);
    try {
      await communityApi.reportPost(postId, reason);
      toast('Đã gửi báo cáo, cảm ơn bạn');
    } catch {
      toast('Chưa gửi được báo cáo, thử lại sau');
    }
  };

  const mine = !!post && post.author.id === me?.id;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'min(780px, 86vh)', maxHeight: '100%', background: 'var(--surface-page)' }}>
      <SocialHeader
        title={`Bài của ${shortName(authorName)}`}
        onBack={onClose}
        right={mine ? undefined : <SocialIconButton name="flag" label="Báo cáo" color="var(--text-secondary)" onClick={() => setReportOpen(true)} />}
      />
      {loaded.loading && !post ? (
        <LoadingState />
      ) : loaded.error || !post ? (
        <ErrorState message="Không tải được bài viết" onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <div style={{ padding: '16px 16px 4px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <AuthorRow author={post.author} time={relativeTime(post.createdAt)} />
              <PostBadges post={post} />
              <div style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ink)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{post.body}</div>
              <PostAttachmentView attachment={post.attachment} />
              <PostActions post={post} onLike={likePost} />
            </div>
            <div
              style={{
                background: 'var(--surface-tint)',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--text-secondary)',
                borderTop: '1px solid var(--border-default)',
              }}
            >
              {`${post.commentCount} bình luận`}
            </div>
            <div style={{ padding: '4px 16px 16px' }}>
              {post.comments.map((c, i) => (
                <CommentRow
                  key={c.id}
                  comment={c}
                  last={i === post.comments.length - 1}
                  canPickBest={mine && post.type === 'QA'}
                  mine={c.author.id === me?.id}
                  onLike={() => likeComment(c)}
                  onReply={() => setText(`@${shortName(c.author.name)} `)}
                  onPickBest={() => pickBest(c)}
                  onDelete={() => removeComment(c)}
                />
              ))}
              {post.comments.length === 0 && (
                <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 14, color: 'var(--text-secondary)' }}>Chưa có bình luận. Bạn là người đầu tiên?</div>
              )}
            </div>
          </div>
          <Composer value={text} onChange={setText} onSend={send} sending={sending} placeholder="Viết bình luận…" />
        </>
      )}
      {reportOpen && <ReportDialog onClose={() => setReportOpen(false)} onReport={report} />}
    </div>
  );
}
